import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IconCheck, IconSearch } from '../components/icons'
import { Button, EmptyState, Field, ListSkeleton, Skeleton } from '../components/ui'
import { api } from '../lib/api'
import { planPrice } from '../lib/format'
import { useToast } from '../lib/toast'
import type {
  BlueprintApplied,
  BlueprintService,
  Detection,
  PlanTier,
  Repository,
  ServiceType,
} from '../lib/types'
import { useApi } from '../lib/useApi'

/**
 * Two steps: pick a repository, then confirm what we inferred from it.
 * Everything on step two has a working default, so the fast path is
 * pick-repo, press Deploy.
 */
export default function NewService() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  useEffect(() => {
    const timer = window.setTimeout(() => { setQuery(search.trim()); setPage(1) }, 350)
    return () => window.clearTimeout(timer)
  }, [search])
  const [repo, setRepo] = useState<Repository | null>(null)

  const { data: repos, error, errorStatus, loading, reload } = useApi(() => api.repos(query, page), [query, page])

  if (repo) {
    return (
      <ConfigureStep
        repo={repo}
        onBack={() => setRepo(null)}
        onDone={async (id) => {
          try {
            const projects = await api.projects()
            const project = projects.find(p => p.services.some(s => s.id === id))
            navigate(project ? `/projects/${project.id}` : `/services/${id}`)
          } catch { navigate(`/services/${id}`) }
        }}
      />
    )
  }

  const filtered = repos

  return (
    <>
      <header className="page-head">
        <div className="titles">
          <h1>New application</h1>
          <p className="subtitle">
            Pick a repository — TalariCloud detects the language and builds it.
          </p>
        </div>
      </header>

      {error && (
        <div className="alert error">
          <span className="grow">{error}</span>
          {errorStatus === 428 ? (
            <Link className="btn sm" to="/account">Connect GitHub</Link>
          ) : <Button onClick={reload}>Retry</Button>}
        </div>
      )}

      <div className="row" style={{ gap: 8 }}>
        <IconSearch className="nav-icon" />
        <input
          className="grow"
          placeholder="Search repositories…"
          value={search}
          autoFocus
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search repositories"
        />
      </div>

      {loading && <ListSkeleton rows={4} />}

      {filtered && filtered.length > 0 && (
        <div className="list">
          {filtered.map((item) => (
            <button
              key={item.full_name}
              className="list-item"
              style={{
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
              }}
              onClick={() => setRepo(item)}
            >
              <div className="grow">
                <div className="row" style={{ gap: 8 }}>
                  <span className="truncate">{item.full_name}</span>
                  {item.private && <span className="pill idle">private</span>}
                </div>
                <div className="tiny faint truncate">
                  {item.description ?? 'No description'}
                </div>
              </div>
              <span className="tiny faint">{item.language ?? '—'}</span>
            </button>
          ))}
        </div>
      )}

      {filtered?.length === 0 && !loading && (
        <EmptyState title="No repositories match">
          Nothing here matches “{search}”. Try a different search, or check the
          account you connected.
        </EmptyState>
      )}
      <nav className="row" aria-label="Repository pages">
        <Button disabled={loading || page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
        <span className="tiny muted">Page {page}</span>
        <Button disabled={loading || (repos?.length ?? 0) < 50} onClick={() => setPage(p => p + 1)}>Next</Button>
      </nav>
    </>
  )
}

function ConfigureStep({
  repo,
  onBack,
  onDone,
}: {
  repo: Repository
  onBack: () => void
  onDone: (serviceId: string) => void
}) {
  const { attempt, notify } = useToast()
  const [name, setName] = useState(repo.name)
  const [branch, setBranch] = useState(repo.default_branch)
  const [type, setType] = useState<ServiceType>('web')
  const [plan, setPlan] = useState<PlanTier>('starter')
  const [port, setPort] = useState(8080)
  const [rootDirectory, setRootDirectory] = useState('')
  const [buildCommand, setBuildCommand] = useState('')
  const [startCommand, setStartCommand] = useState('')
  const [preDeployCommand, setPreDeployCommand] = useState('')
  const [publishDirectory, setPublishDirectory] = useState('')
  const [cronSchedule, setCronSchedule] = useState('0 * * * *')
  const [envText, setEnvText] = useState('')
  const [busy, setBusy] = useState(false)
  const [edited, setEdited] = useState(false)
  // Set only by a blueprint; otherwise what detection found is used.
  const [dockerfilePath, setDockerfilePath] = useState<string | null>(null)
  const [healthCheckPath, setHealthCheckPath] = useState('/')
  const [disk, setDisk] = useState<{ size: number; path: string | null }>({ size: 0, path: null })
  const [appliedBlueprint, setAppliedBlueprint] = useState<string | null>(null)

  const { data: branches } = useApi(() => api.branches(repo.full_name), [repo.full_name])
  const { data: plans } = useApi(() => api.plans(), [])


  // Read the repository and fill the form in from what is actually in it. It
  // re-runs when the branch or the monorepo directory changes, because those
  // are exactly the changes that alter the answer.
  const {
    data: detection,
    loading: detecting,
    error: detectionError,
  } = useApi(
    () => api.detect(repo.full_name, branch, rootDirectory || undefined),
    [repo.full_name, branch, rootDirectory],
  )

  // How many apps this repository holds, when it holds more than one. Every
  // other platform creates all of them; creating one and leaving the rest is
  // the unusual thing to want, so the form below says so rather than
  // presenting the two as equals.
  const several =
    (detection?.blueprint.length || detection?.candidates.length || 0) > 1
      ? detection?.blueprint.length || detection?.candidates.length
      : 0

  // Applied once per detection, and never over something the developer typed:
  // a form that overwrites your edits a second after you make them is worse
  // than one that never helped.
  useEffect(() => {
    if (!detection) return
    // The app was found one folder down — a company repository with the site
    // in `website/`. Filling Root directory in re-runs detection there, which
    // is what fills the rest of the form. Only ever into an empty field: a
    // developer who typed a folder knows better than the search did.
    if (detection.root_directory && !rootDirectory) {
      setRootDirectory(detection.root_directory)
      return
    }
    if (edited) return
    setType(detection.type)
    setPort(detection.port)
    setBuildCommand(detection.build_command ?? '')
    setStartCommand(detection.start_command ?? '')
    setPublishDirectory(detection.publish_directory ?? '')
  }, [detection, edited, rootDirectory])

  // Fill the whole form from one service of the repository's render.yaml —
  // the same settings Render runs it with, so nothing is retyped. Marks the
  // form as edited so a later detection does not overwrite what the file said.
  const applyBlueprint = (service: BlueprintService) => {
    setEdited(true)
    setAppliedBlueprint(service.name)
    setName(service.name)
    setType(service.type)
    setRootDirectory(service.root_directory)
    setBuildCommand(service.build_command ?? '')
    setStartCommand(service.start_command ?? '')
    setPreDeployCommand(service.pre_deploy_command ?? '')
    setPublishDirectory(service.publish_directory ?? '')
    setDockerfilePath(service.dockerfile_path)
    setHealthCheckPath(service.health_check_path ?? '/')
    setDisk({ size: service.disk_size_gb, path: service.disk_mount_path })
    if (service.cron_schedule) setCronSchedule(service.cron_schedule)
    if (service.branch && branches?.includes(service.branch)) setBranch(service.branch)
    const lines = Object.entries(service.env).map(([key, value]) => `${key}=${value}`)
    // Render generated these; a fresh random value serves the same purpose.
    for (const key of service.env_generated) lines.push(`${key}=${randomSecret()}`)
    // Render prompted for these; leave them visible and empty so they are
    // filled in rather than forgotten.
    for (const key of service.env_needed) lines.push(`${key}=`)
    setEnvText(lines.join('\n'))
  }

  // Create everything the render.yaml names, in one go — the API, the site,
  // the Postgres they share, each deployed. This is what applying a blueprint
  // does on Render, and it is the reason a repository that "works there"
  // needed four visits to this form to work here.
  const applyWholeBlueprint = async () => {
    if (!detection) return
    setBusy(true)
    let result: BlueprintApplied | null = null
    await attempt(
      async () => {
        result = await api.applyBlueprint({
          repo_full_name: repo.full_name,
          branch,
          plan,
          deploy_now: true,
        })
      },
      {
        success: 'Blueprint applied — everything it names is building',
        failure: 'Could not apply the blueprint',
      },
    )
    setBusy(false)
    if (!result) return
    const applied: BlueprintApplied = result
    // Anything the file said Render would prompt for is still missing, and an
    // app that needs it will not start. Say so once, by name, rather than
    // letting it be discovered in a crash log.
    const missing = Object.entries(applied.needs_values)
    for (const [service, keys] of missing) {
      notify(
        `${service} still needs ${keys.join(', ')} — set them under Environment`,
        'info',
      )
    }
    if (applied.services.length > 0) onDone(applied.services[0].id)
    else onBack()
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    let created: string | null = null
    await attempt(
      async () => {
        const service = await api.createService({
          name,
          repo_full_name: repo.full_name,
          repo_url: repo.clone_url,
          branch,
          type,
          plan,
          port,
          root_directory: rootDirectory,
          build_command: buildCommand || null,
          start_command: startCommand || null,
          pre_deploy_command: preDeployCommand || null,
          publish_directory: publishDirectory || null,
          dockerfile_path: dockerfilePath ?? detection?.dockerfile_path ?? null,
          health_check_path: healthCheckPath || '/',
          disk_size_gb: disk.size,
          disk_mount_path: disk.size > 0 ? disk.path : null,
          cron_schedule: type === 'cron' ? cronSchedule : null,
          env_vars: parseEnv(envText),
          deploy_now: true,
        })
        created = service.id
      },
      { success: `${name} created — building now`, failure: 'Could not create the service' },
    )
    setBusy(false)
    if (created) onDone(created)
  }

  return (
    <>
      <header className="page-head">
        <div className="titles">
          <h1 className="mono">{repo.full_name}</h1>
          <p className="subtitle">
            Filled in from the repository — change anything you disagree with.
          </p>
        </div>
        <Button variant="ghost" onClick={onBack}>
          Pick another repo
        </Button>
      </header>

      <DetectionCard
        detection={detection}
        loading={detecting}
        error={detectionError}
      />

      {detection && (detection.blueprint.length > 0 || servicesInRepo(detection) > 1) && (
        <DeployEverything
          detection={detection}
          applied={appliedBlueprint}
          onPick={applyBlueprint}
          onApplyAll={applyWholeBlueprint}
          busy={busy}
        />
      )}

      {detection && detection.candidates.length > 0 && !appliedBlueprint && (
        <div className="card detect unsure">
          <div className="grow stack" style={{ gap: 8 }}>
            <strong>Or create just one of them — which app is this service?</strong>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {detection.candidates.map((folder) => (
                <Button
                  key={folder}
                  variant={rootDirectory === folder ? 'primary' : 'ghost'}
                  type="button"
                  onClick={() => setRootDirectory(folder)}
                >
                  <span className="mono">{folder}/</span>
                </Button>
              ))}
            </div>
            <p className="tiny muted">
              Each app becomes its own service, with its folder as Root directory
              — the same as on Render.
            </p>
          </div>
        </div>
      )}

      <form className="card card-pad-lg stack" onSubmit={submit}>
        <div className="field-row">
          <Field
            label="Service name"
            htmlFor="name"
            hint={`Becomes your URL: ${slugPreview(name)}.talaricloud.com`}
          >
            <input
              id="name"
              value={name}
              required
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field
            label="Branch"
            htmlFor="branch"
            hint="Every push here redeploys automatically."
          >
            <select
              id="branch"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
            >
              {(branches ?? [repo.default_branch]).map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="field-row">
          {/*
            No type to choose. What a repository is, is a fact about the
            repository — a site that builds to files is a static site and an
            app that listens on a port is a web service — and no platform
            worth using asks a person to classify their own code before it
            will look at it. It is detected, said out loud here so the answer
            is visible rather than merely assumed, and changeable in Settings
            on the rare occasion the reading is wrong.
          */}
          <Field label="What this is" htmlFor="detected-type">
            <output id="detected-type" style={{ padding: '9px 0', display: 'block' }}>
              {detecting ? 'Reading the repository…' : (DETECTED_TYPE[type] ?? type)}
            </output>
          </Field>

          <Field label="Instance" htmlFor="plan">
            <select
              id="plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value as PlanTier)}
            >
              {(plans ?? []).map((item) => (
                <option key={item.tier} value={item.tier}>
                  {item.label} — {item.cpu} CPU, {item.memory} ·{' '}
                  {Number(item.monthly_etb) === 0
                    ? 'free'
                    : `${planPrice(item)}/mo`}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {type === 'cron' && (
          <Field label="Schedule (cron, East Africa Time)" htmlFor="cron">
            <input
              id="cron"
              className="mono"
              value={cronSchedule}
              onChange={(e) => setCronSchedule(e.target.value)}
            />
          </Field>
        )}

        {type === 'function' && (
          <p className="tiny muted" style={{ marginTop: -6 }}>
            Add a <span className="mono">handler.js</span> or{' '}
            <span className="mono">handler.py</span> that exports a function
            taking the request — TalariCloud wraps it, serves it at a URL, and
            manages the port and health checks for you.
          </p>
        )}

        {(type === 'web' || type === 'static') && (
          <Field
            label="Port your app listens on"
            htmlFor="port"
            hint="TalariCloud sets PORT in your environment — read it if you can."
          >
            <input
              id="port"
              type="number"
              className="tabular"
              value={port}
              min={1}
              max={65535}
              onChange={(e) => {
                setEdited(true)
                setPort(Number(e.target.value))
              }}
            />
          </Field>
        )}

        {type === 'static' && (
          <Field
            label="Publish directory"
            htmlFor="publish"
            hint="The folder your build writes into — this is what gets served."
          >
            <input
              id="publish"
              className="mono"
              placeholder="dist"
              value={publishDirectory}
              onChange={(e) => {
                setEdited(true)
                setPublishDirectory(e.target.value)
              }}
            />
          </Field>
        )}

        {/* Open when detection has something to show here. Hiding the command
            that will actually run behind a disclosure is how a developer ends
            up debugging a start command they never saw. */}
        <details open={Boolean(buildCommand || startCommand || preDeployCommand) || detection?.confident === false}>
          <summary className="muted" style={{ cursor: 'pointer', marginBottom: 12 }}>
            Build, start and monorepo settings
          </summary>

          <div className="stack">
            <Field
              label="Root directory"
              htmlFor="root"
              hint="Leave blank unless this is a monorepo."
            >
              <input
                id="root"
                className="mono"
                placeholder="apps/api"
                value={rootDirectory}
                onChange={(e) => setRootDirectory(e.target.value)}
              />
            </Field>

            <Field label="Build command" htmlFor="build">
              <input
                id="build"
                className="mono"
                placeholder="detected automatically"
                value={buildCommand}
                onChange={(e) => {
                  setEdited(true)
                  setBuildCommand(e.target.value)
                }}
              />
            </Field>

            <Field label="Start command" htmlFor="start">
              <input
                id="start"
                className="mono"
                placeholder="detected automatically"
                value={startCommand}
                onChange={(e) => {
                  setEdited(true)
                  setStartCommand(e.target.value)
                }}
              />
            </Field>
            <Field label="Pre-deploy command" htmlFor="pre-deploy"
              hint="Optional migrations. Runs after databases are ready, before the new app starts. It has no access to the service disk.">
              <input id="pre-deploy" className="mono" value={preDeployCommand}
                placeholder="e.g. npm run migrate"
                onChange={(e) => { setEdited(true); setPreDeployCommand(e.target.value) }} />
            </Field>
          </div>
        </details>

        <Field
          label="Environment variables"
          htmlFor="env"
          hint="One KEY=value per line. Stored encrypted."
        >
          <textarea
            id="env"
            className="mono"
            rows={4}
            placeholder={'DATABASE_URL=postgres://…\nAPI_KEY=…'}
            value={envText}
            onChange={(e) => setEnvText(e.target.value)}
          />
        </Field>

        <div className="row" style={{ gap: 12 }}>
          <Button variant={several ? "secondary" : "primary"} type="submit" loading={busy}>
            {several ? `Deploy only ${name}` : 'Create and deploy'}
          </Button>
          {several && (
            <span className="tiny faint">
              This repository holds {several} apps — deploying all of them is
              the button above.
            </span>
          )}
        </div>
      </form>
    </>
  )
}

/**
 * What reading the repository turned up, in a sentence.
 *
 * Placed above the form deliberately: a developer should be told what is about
 * to happen before being asked to confirm it, and most of the time the honest
 * answer is "we already know, press the button".
 */
function DetectionCard({
  detection,
  loading,
  error,
}: {
  detection: Detection | null
  loading: boolean
  error: string | null
}) {
  if (loading && !detection) {
    return (
      <div className="card detect">
        <Skeleton width={22} height={22} radius={999} />
        <div className="stack" style={{ gap: 8, flex: 1 }}>
          <Skeleton width={220} height={13} />
          <Skeleton width={320} height={11} />
        </div>
      </div>
    )
  }

  // Detection is a convenience, not a gate — a repository we could not read
  // still deploys, so this never blocks the form.
  if (error || !detection) {
    return (
      <div className="alert info">
        We could not read this repository ahead of time, so the fields below are
        plain defaults. The build will still work it out.
      </div>
    )
  }

  return (
    <div className={`card detect ${detection.confident ? 'sure' : 'unsure'}`}>
      <span className="detect-mark" aria-hidden="true">
        {detection.confident ? <IconCheck className="" size={13} /> : '?'}
      </span>

      <div className="grow" style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <strong>{detection.runtime}</strong>
          {detection.evidence.length > 0 && (
            <span className="tiny faint mono">
              from {detection.evidence.join(', ')}
            </span>
          )}
        </div>

        <p className="tiny muted" style={{ marginTop: 4 }}>
          {detection.type === 'static'
            ? 'Built once and served as static files'
            : `Runs as a web service on port ${detection.port}`}
          {detection.start_command && (
            <>
              {' · '}
              <span className="mono">{detection.start_command}</span>
            </>
          )}
        </p>

        {detection.notes.map((note) => (
          <p className="tiny faint" key={note} style={{ marginTop: 6 }}>
            {note}
          </p>
        ))}
      </div>
    </div>
  )
}

/**
 * Everything the repository holds, deployed in one press.
 *
 * A render.yaml when there is one — that is why a repository "works on Render",
 * and applying it here reproduces it exactly. Without one the repository is
 * read instead and every app in it is created, so a repository with an API and
 * a marketing site is one press either way rather than two trips through this
 * form. Picking a single service is still offered underneath.
 */
/** The platform's name for a directory holding nothing it can build. Mirrors
 *  `NOT_RECOGNISED` in control-plane/app/services/detect.py. */
const NOT_RECOGNISED = 'Not recognised'

/** Whether the top of the repository is itself one of the services.
 *
 *  It usually is, and it is not in `candidates` — that list is the folders
 *  beside it. Counting only the folders is how an API at the root with a
 *  frontend next to it was offered as "1 app" when applying would create two.
 */
function rootIsAService(detection: Detection): boolean {
  return detection.runtime !== NOT_RECOGNISED
}

/** How many services applying this repository would create. */
function servicesInRepo(detection: Detection): number {
  return detection.candidates.length + (rootIsAService(detection) ? 1 : 0)
}

function DeployEverything({
  detection,
  applied,
  onPick,
  onApplyAll,
  busy,
}: {
  detection: Detection
  applied: string | null
  onPick: (service: BlueprintService) => void
  onApplyAll: () => void
  busy: boolean
}) {
  const chosen = detection.blueprint.find((service) => service.name === applied) ?? null
  const fromBlueprint = detection.blueprint.length > 0
  const count = fromBlueprint ? detection.blueprint.length : servicesInRepo(detection)
  const databases = detection.blueprint_databases.length
  const pieces = [
    `${count} ${count === 1 ? 'service' : 'services'}`,
    ...(databases ? [`${databases} ${databases === 1 ? 'database' : 'databases'}`] : []),
  ]
  return (
    <div className="card detect sure">
      <div className="grow stack" style={{ gap: 8 }}>
        <strong>
          {fromBlueprint
            ? `This repository's blueprint describes ${pieces.join(' and ')}.`
            : `This repository holds ${count} apps: ${[
                ...(rootIsAService(detection) ? ['the top level'] : []),
                ...detection.candidates.map((folder) => `${folder}/`),
              ].join(', ')}.`}
        </strong>
        <p className="tiny muted">
          {fromBlueprint
            ? 'Deploy them all and each is created and built together, named and configured the way the file says — the same as applying a blueprint on Render.'
            : 'Deploy them all and each folder becomes its own service, built and served the way it is written. Anything that cannot be served on its own — a mobile app, a shared package — is left alone and named afterwards.'}
        </p>
        <div className="row" style={{ gap: 8 }}>
          <Button variant="primary" type="button" onClick={onApplyAll} loading={busy}>
            {busy ? 'Deploying…' : `Deploy everything — all ${pieces.join(' and ')}`}
          </Button>
        </div>
        {fromBlueprint && <strong className="tiny">Or create just one:</strong>}
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {detection.blueprint.map((service) => (
            <Button
              key={service.name}
              variant={applied === service.name ? 'primary' : 'ghost'}
              type="button"
              onClick={() => onPick(service)}
            >
              <span className="mono">{service.name}</span>
              <span className="tiny faint" style={{ marginLeft: 6 }}>
                {service.type}
                {service.root_directory ? ` · ${service.root_directory}/` : ''}
              </span>
            </Button>
          ))}
        </div>
        {chosen && (
          <p className="tiny muted">
            Filled in from <span className="mono">{chosen.name}</span>. Environment
            variables Render prompted for are listed below with empty values — fill
            them in before you deploy.
          </p>
        )}
        {[...(chosen?.notes ?? []), ...detection.blueprint_notes].map((note) => (
          <p className="tiny faint" key={note}>
            {note}
          </p>
        ))}
        {detection.blueprint_databases.length > 0 && (
          <p className="tiny faint">
            The blueprint also declares {databases === 1 ? 'a database' : 'databases'}:{' '}
            <span className="mono">{detection.blueprint_databases.join(', ')}</span>.
            Applying the blueprint creates {databases === 1 ? 'it' : 'them'} and links
            {databases === 1 ? ' it' : ' them'} to the services that asked for
            {databases === 1 ? ' it' : ' them'}; creating one service at a time does not,
            so do that under Databases.
          </p>
        )}
      </div>
    </div>
  )
}

/** What each detected type means, in the words a person would use. */
const DETECTED_TYPE: Record<string, string> = {
  web: 'Web service — served at its own HTTPS address',
  function: 'Function — a handler, served for you',
  static: 'Static site — built once, served as files',
  worker: 'Background worker — no public address',
  cron: 'Cron job — runs on a schedule',
}

/** A value with the same job as one Render would have generated. */
function randomSecret(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function parseEnv(text: string): Record<string, string> {
  const result: Record<string, string> = {}
  for (const line of text.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed || trimmed.startsWith('#')) continue
    const index = trimmed.indexOf('=')
    if (index < 1) continue
    // Values may legitimately contain '=', so only split on the first one.
    result[trimmed.slice(0, index).trim()] = trimmed.slice(index + 1).trim()
  }
  return result
}

function slugPreview(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-|-$/g, '') || 'your-app'
  )
}
